/**
 * src/context/NotificationContext.tsx
 * Owns the single live SSE connection (one EventSource per authenticated
 * session, opened while the operator is logged in) and turns each incoming
 * event into two things: a toast (via useToast, so it reuses the existing
 * toast stack/animation) and an entry in a running notification list for
 * NotificationBell's dropdown + unread badge.
 *
 * Per-user filtering happens server-side: notification-service only sends a
 * given event down this user's stream if their Settings > Notifications
 * toggles allow it, so nothing is filtered here. Events that arrive while
 * the operator is offline are replayed on next login by MissedAlertsPanel.
 *
 * Native EventSource reconnects on its own after a dropped connection (no
 * custom backoff needed here); onerror just reflects connection state for
 * anything that wants to show it, it does not need to manually reconnect.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { NOTIFICATIONS_STREAM_URL } from '@/lib/services/notifications';

export type LiveNotificationType =
  | 'booking.ready_for_review'
  | 'email.received'
  | 'thread_reply.received'
  | 'workflow.failed'
  | 'workflow.quarantined'
  | 'substitution.required'
  | 'audit.alert';

const LIVE_NOTIFICATION_TYPES: LiveNotificationType[] = [
  'booking.ready_for_review',
  'email.received',
  'thread_reply.received',
  'workflow.failed',
  'workflow.quarantined',
  'substitution.required',
  'audit.alert',
];

// These need attention (warning tone); everything else is routine
// informational activity (info tone). substitution.required blocks a quote
// until an operator acts, and audit.alert is a security/role-sensitive
// event, so both are treated as attention-worthy.
const ERROR_TONE_TYPES: ReadonlySet<LiveNotificationType> = new Set([
  'workflow.failed',
  'workflow.quarantined',
  'substitution.required',
  'audit.alert',
]);

export type LiveNotification = {
  id: string;
  type: LiveNotificationType;
  workflowId: string;
  title: string;
  body: string;
  receivedAt: number;
  read: boolean;
};

type RawLiveEvent = {
  type: LiveNotificationType;
  workflow_id: string;
  title: string;
  body: string;
};

const MAX_NOTIFICATIONS = 50;

// Persisted per-tab so a page reload (or a brief isAuthenticated flip during
// a token refresh) doesn't silently wipe the dropdown/badge state out from
// under a toast that already fired and disappeared. sessionStorage (not
// localStorage) is deliberate — this list is a live-session convenience,
// not a durable record; the audit log is the durable source of truth.
const STORAGE_KEY = 'glowhire-live-notifications';

function loadStoredNotifications(): LiveNotification[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LiveNotification[]) : [];
  } catch {
    return [];
  }
}

function persistNotifications(notifications: LiveNotification[]) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  } catch {
    // Storage unavailable (private mode, quota, etc.) — badge/dropdown still
    // work for the current page load, they just won't survive a reload.
  }
}

type NotificationContextValue = {
  notifications: LiveNotification[];
  unreadCount: number;
  isConnected: boolean;
  markAllRead: () => void;
  markRead: (id: string) => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [notifications, setNotifications] = useState<LiveNotification[]>(() => loadStoredNotifications());
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Mirror every change to sessionStorage so a reload rehydrates the list
  // instead of silently dropping it (see STORAGE_KEY comment above).
  useEffect(() => {
    persistNotifications(notifications);
  }, [notifications]);

  useEffect(() => {
    if (!isAuthenticated) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      setIsConnected(false);
      return;
    }

    const source = new EventSource(NOTIFICATIONS_STREAM_URL, { withCredentials: true });
    eventSourceRef.current = source;

    source.onopen = () => setIsConnected(true);
    source.onerror = () => setIsConnected(false);

    const handleEvent = (type: LiveNotificationType) => (evt: MessageEvent<string>) => {
      let raw: RawLiveEvent;
      try {
        raw = JSON.parse(evt.data);
      } catch {
        return;
      }

      const entry: LiveNotification = {
        id: `${type}-${raw.workflow_id}-${Date.now()}`,
        type,
        workflowId: raw.workflow_id,
        title: raw.title,
        body: raw.body,
        receivedAt: Date.now(),
        read: false,
      };

      setNotifications((prev) => [entry, ...prev].slice(0, MAX_NOTIFICATIONS));

      if (ERROR_TONE_TYPES.has(type)) {
        toast.warning(`${raw.title} — ${raw.body}`);
      } else {
        toast.info(`${raw.title} — ${raw.body}`);
      }
    };

    const listeners = LIVE_NOTIFICATION_TYPES.map((type) => {
      const listener = handleEvent(type);
      source.addEventListener(type, listener as EventListener);
      return { type, listener };
    });

    return () => {
      listeners.forEach(({ type, listener }) => source.removeEventListener(type, listener as EventListener));
      source.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast identity is stable per ToastProvider render, re-subscribing on it would tear down the connection for no reason
  }, [isAuthenticated]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      isConnected,
      markAllRead: () => setNotifications((prev) => prev.map((n) => ({ ...n, read: true }))),
      markRead: (id: string) => setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n))),
      // Removes the notification from the list entirely, rather than just
      // dimming it — NotificationBell wires this to a click on an item so it
      // actually disappears once acted on, instead of only ever getting
      // marked read. Added 28 Sep 2026: there was previously no action that
      // removed a notification at all, only markRead/markAllRead's dimming.
      dismiss: (id: string) => setNotifications((prev) => prev.filter((n) => n.id !== id)),
      clearAll: () => setNotifications([]),
    }),
    [notifications, unreadCount, isConnected],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
}
